import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, MessageSquareText, Search, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { exportToCSV } from "@/lib/csv-export";
import { cn } from "@/lib/utils";
import {
  panelControl,
  panelDialog,
  panelPill,
} from "@/components/producer/panel";

export function QuestionAnswersModal({
  isOpen,
  onClose,
  question,
}: {
  isOpen: boolean;
  onClose: () => void;
  question: any | null;
}) {
  const [search, setSearch] = useState("");

  const { data: answers = [], isLoading } = useQuery({
    queryKey: ["question-answers", question?.id],
    queryFn: async () => {
      if (!question?.id) return [];

      const { data, error } = await supabase
        .from("checkout_answers")
        .select(`
          id,
          answer,
          created_at,
          ticket_id,
          order_id,
          tickets (
            id,
            attendee_name,
            attendee_email,
            attendee_cpf,
            ticket_tiers ( name )
          ),
          orders (
            id,
            buyer_name,
            buyer_email,
            buyer_cpf,
            profiles ( full_name, email )
          )
        `)
        .eq("question_id", question.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching question answers:", error);
        return [];
      }

      return (data || []).map((item: any) => {
        const ticket = Array.isArray(item.tickets) ? item.tickets[0] : item.tickets;
        const order = Array.isArray(item.orders) ? item.orders[0] : item.orders;

        const name =
          ticket?.attendee_name ||
          order?.buyer_name ||
          order?.profiles?.full_name ||
          "Participante Sem Nome";

        const email =
          ticket?.attendee_email ||
          order?.buyer_email ||
          order?.profiles?.email ||
          "—";

        const tierName = ticket?.ticket_tiers?.name || "1x por Pedido";

        return {
          id: item.id,
          answer: item.answer || "—",
          createdAt: item.created_at,
          name,
          email,
          tierName,
        };
      });
    },
    enabled: isOpen && !!question?.id,
  });

  const filteredAnswers = useMemo(() => {
    if (!search.trim()) return answers;
    const term = search.toLowerCase();
    return answers.filter(
      (a) =>
        a.name.toLowerCase().includes(term) ||
        a.email.toLowerCase().includes(term) ||
        a.tierName.toLowerCase().includes(term) ||
        a.answer.toLowerCase().includes(term)
    );
  }, [answers, search]);

  const handleExportCSV = () => {
    if (!answers.length) return;
    const safeTitle = (question?.question || "pergunta")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_");

    exportToCSV(
      answers,
      [
        { key: "name", header: "Nome" },
        { key: "email", header: "E-mail" },
        { key: "tierName", header: "Ingresso / Lote" },
        { key: "answer", header: "Resposta" },
        {
          key: "createdAt",
          header: "Data da Resposta",
          format: (v: string) =>
            v ? new Date(v).toLocaleString("pt-BR") : "",
        },
      ],
      `respostas_${safeTitle}`
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent mobilePresentation="fullscreen" className={cn(panelDialog, "flex max-h-[85vh] max-w-2xl flex-col")}>
        {/* Header */}
        <div className="pl-6 pr-14 py-4 border-b border-border/50 bg-background/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-2">
              <MessageSquareText className="w-4 h-4 text-muted-foreground shrink-0" />
              <h2 className="text-base font-semibold tracking-tight text-foreground truncate">
                {question?.question || "Respostas da Pergunta"}
              </h2>
            </div>
            <p className="text-xs text-muted-foreground/80 mt-0.5">
              {answers.length} resposta(s) registrada(s) até o momento.
            </p>
          </div>

          <Button
            size="sm"
            onClick={handleExportCSV}
            disabled={!answers.length}
            className={cn("h-9 shrink-0 gap-1.5 px-4 text-xs font-semibold", panelPill)}
          >
            <Download className="w-3.5 h-3.5" /> Baixar Excel
          </Button>
        </div>

        {/* Search Toolbar */}
        <div className="p-4 border-b border-border/40 bg-muted/20 shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              type="search"
              name="search-answers-query"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por participante, e-mail, ingresso ou resposta..."
              className={cn("pl-9", panelControl, "mt-0")}
            />
          </div>
        </div>

        {/* Answers List */}
        <div className="flex-1 overflow-y-auto min-h-[250px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground/50" />
              <p className="text-xs text-muted-foreground">Carregando respostas...</p>
            </div>
          ) : filteredAnswers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4 space-y-2">
              <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center text-muted-foreground/60">
                <MessageSquareText className="w-5 h-5" />
              </div>
              <p className="text-sm font-medium text-foreground">
                {search ? "Nenhuma resposta corresponde à busca" : "Nenhuma resposta registrada"}
              </p>
              <p className="text-xs text-muted-foreground max-w-sm">
                {search
                  ? "Tente buscar por outro termo ou nome de participante."
                  : "As respostas aparecerão aqui conforme os participantes concluírem as compras."}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {filteredAnswers.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-border/50 bg-card/60 p-4 space-y-2.5 transition-all hover:border-border hover:bg-card"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-foreground truncate">{item.name}</span>
                      <span className="text-muted-foreground/70 text-xs font-normal truncate">({item.email})</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className="text-[10px] font-medium bg-muted/80 text-muted-foreground rounded-md px-2 py-0.5 border-0">
                        {item.tierName}
                      </Badge>
                      {item.createdAt && (
                        <span className="text-[11px] text-muted-foreground/60">
                          {new Date(item.createdAt).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-lg p-3 border border-border/40 text-sm text-foreground whitespace-pre-wrap leading-relaxed font-normal">
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border/40 bg-background/80 flex items-center justify-between text-xs text-muted-foreground shrink-0">
          <span>Mostrando {filteredAnswers.length} de {answers.length} respostas</span>
          <Button variant="ghost" size="sm" onClick={onClose} className="rounded-lg h-8 px-3 text-xs font-medium hover:bg-muted transition-all active:scale-[0.97]">
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
